import { useRef } from 'react';
import { Space, Typography, Container } from '@ag1/ui';
import { SanityKeyed, ValueReviewCards as ValueReviewCardsProps } from 'types/studio/studio';
import { navigationClickEvent } from 'analytics/events/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import clsx from 'clsx';
import useAddAccessibility from 'shared/hooks/addAccessibility';
import { ReviewCard } from './ReviewCard';
import CustomPortableText from '../PortableText/PortableText';
import styles from './ValueReviewCards.module.scss';
import IngredientModalSection from '../IngredientModalSection/IngredientModalSection';
import { CommonSectionProps } from '../types';

const ValueReviewCards = ({
  _key: key,
  _type: sectionType,
  superHeadline,
  headline,
  headlineBody,
  cards,
  unifiedButton,
  padding,
  visibleOnPageLoad
}: SanityKeyed<ValueReviewCardsProps> & CommonSectionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useAddAccessibility(contentRef, true);
  const sendNavigationEvent = (index: number, cardHeadline: string) => {
    navigationClickEvent({
      location: `${sectionType} ${index + 1}`,
      navigationType: 'accordion',
      clickText: 'expand',
      header: headline,
      subheader: cardHeadline
    });
  };

  const isIngredientsModal =
    unifiedButton?.switchButton &&
    (unifiedButton.openModalSelect === 'ingredients' ||
      unifiedButton.openModalSelect === 'ingredientsModal');

  return (
    <Container narrow id={key}>
      <Space
        top={{ xxs: 40, s: 60, m: 80, l: 100 }}
        bottom={{ xxs: 40, s: 60, m: 80, l: 100 }}
        className={clsx(getSectionPaddingClasses(styles, padding))}>
        {headline && (
          <div className={styles.headlineBlock}>
            <div ref={contentRef}>
              {superHeadline && (
                <Typography
                  variant="subtitle"
                  size="2"
                  component="p"
                  noPadding
                  className={styles.superHeadline}
                  color="black-new">
                  {superHeadline}
                </Typography>
              )}
              <Typography
                variant="headlineSans"
                size="2"
                component="h2"
                noPadding
                color="black-new">
                {headline}
              </Typography>
              {headlineBody && (
                <Typography
                  variant="subtitle"
                  size="1"
                  component="div"
                  noPadding
                  className={styles.headlineBody}
                  color="black-new">
                  <CustomPortableText value={headlineBody} />
                </Typography>
              )}
            </div>
            {isIngredientsModal && (
              <IngredientModalSection
                ingredientModalDetail={unifiedButton as any}
                sectionType={sectionType}
                className={styles.headlineButton}
                size="medium"
              />
            )}
          </div>
        )}
        <div className={styles.cards}>
          {cards.map((item, index) => (
            <ReviewCard
              key={item._key}
              {...item}
              sendNavigationEvent={() => sendNavigationEvent(index, item.headline)}
              hotspotImageSizes={{ xxs: '100vw', s: '45vw', l: '700px' }}
              visibleOnPageLoad={visibleOnPageLoad}
            />
          ))}
        </div>
      </Space>
    </Container>
  );
};

export default ValueReviewCards;
